/**
 * AIInsights Component
 *
 * AI-powered insights and recommendations based on analytics data
 * Provides contextual analysis and actionable recommendations
 * Collapsible card with interactive data point references
 */

import React, { useState } from 'react';
import { Box, Typography, Paper, Chip, Divider, Collapse, IconButton } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

export interface Insight {
  type: 'positive' | 'concern' | 'neutral';
  title: string;
  description: string;
  dataPoints?: string[]; // References to specific data elements
}

export interface Recommendation {
  priority: 'high' | 'medium' | 'low';
  title: string;
  action: string;
  impact: string;
  dataPoints?: string[]; // References to specific data elements
}

interface AIInsightsProps {
  insights: Insight[];
  recommendations: Recommendation[];
  summary: string;
  onDataPointClick?: (dataPoint: string) => void;
  onExpandedChange?: (expanded: boolean) => void;
}

const AIInsights: React.FC<AIInsightsProps> = ({ insights, recommendations, summary, onDataPointClick, onExpandedChange }) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandToggle = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (onExpandedChange) {
      onExpandedChange(newExpanded);
    }
  };

  const getInsightIcon = (type: Insight['type']) => {
    switch (type) {
      case 'positive':
        return <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />;
      case 'concern':
        return <WarningAmberIcon sx={{ fontSize: 20, color: 'warning.main' }} />;
      case 'neutral':
        return <TrendingUpIcon sx={{ fontSize: 20, color: 'info.main' }} />;
    }
  };

  const getPriorityColor = (priority: Recommendation['priority']) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
    }
  };

  // Count total insights and recommendations
  const totalInsights = insights.length;
  const totalRecommendations = recommendations.length;
  const urgentRecommendations = recommendations.filter(r => r.priority === 'high').length;

  // Make text with data point references clickable
  const renderTextWithDataPoints = (text: string, dataPoints?: string[]) => {
    if (!dataPoints || dataPoints.length === 0 || !onDataPointClick) {
      return <span>{text}</span>;
    }

    let processedText = text;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Find and replace data point references
    dataPoints.forEach((dataPoint, dpIndex) => {
      const regex = new RegExp(dataPoint, 'gi');
      const match = regex.exec(processedText);

      if (match) {
        const matchIndex = match.index;

        // Add text before match
        if (matchIndex > lastIndex) {
          parts.push(<span key={`text-${dpIndex}`}>{processedText.substring(lastIndex, matchIndex)}</span>);
        }

        // Add clickable data point
        parts.push(
          <Box
            component="span"
            key={`dp-${dpIndex}`}
            onClick={(e) => {
              e.stopPropagation();
              onDataPointClick(dataPoint);
            }}
            sx={{
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textDecorationColor: 'primary.main',
              cursor: 'pointer',
              fontWeight: 600,
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline',
                textDecorationStyle: 'solid',
                backgroundColor: 'primary.light',
                borderRadius: '2px',
                padding: '0 2px',
              },
            }}
          >
            {match[0]}
          </Box>
        );

        lastIndex = matchIndex + match[0].length;
      }
    });

    // Add remaining text
    if (lastIndex < processedText.length) {
      parts.push(<span key="text-end">{processedText.substring(lastIndex)}</span>);
    }

    return <>{parts}</>;
  };

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: { xs: '280px', sm: '304px', md: '328px' },
        right: { xs: '72px', sm: '88px', md: '104px' },
        zIndex: 1200,
        background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: '12px 12px 0 0',
        height: expanded ? '45vh' : 'auto',
        maxHeight: expanded ? '45vh' : '72px',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
      }}
    >
      {/* Background pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          opacity: 0.05,
          background: 'radial-gradient(circle, #1976d2 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header - Always Visible */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            py: expanded ? 2 : 1.5,
            px: 2,
            flexShrink: 0,
            minHeight: expanded ? 'auto' : '72px',
          }}
          onClick={handleExpandToggle}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AutoAwesomeIcon sx={{ fontSize: 28, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                AI Insights & Recommendations
              </Typography>
              {!expanded && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {totalInsights} insight{totalInsights !== 1 ? 's' : ''} · {totalRecommendations} recommendation{totalRecommendations !== 1 ? 's' : ''}
                    {urgentRecommendations > 0 && ` · ${urgentRecommendations} urgent`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <IconButton sx={{ color: 'primary.main' }}>
            {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>

        {/* Expandable Content */}
        <Collapse in={expanded} timeout="auto" sx={{ flex: 1, overflow: 'visible' }}>
          <Box sx={{ height: 'calc(45vh - 72px)', overflow: 'auto', px: 3, pb: 3 }}>
            <Divider sx={{ mb: 3, borderColor: 'primary.light' }} />

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, fontStyle: 'italic' }}>
              Click underlined terms to highlight related charts and data
            </Typography>

            {/* Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.primary' }}>
                {summary}
              </Typography>
            </Box>

            {/* Key Insights */}
            {insights.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary' }}>
                  Key Insights
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {insights.map((insight, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                        {getInsightIcon(insight.type)}
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                          {renderTextWithDataPoints(insight.title, insight.dataPoints)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          {renderTextWithDataPoints(insight.description, insight.dataPoints)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, color: 'text.primary' }}>
                  Recommended Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {recommendations.map((rec, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderLeft: '4px solid',
                        borderLeftColor: rec.priority === 'high' ? 'error.main' : rec.priority === 'medium' ? 'warning.main' : 'info.main',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip
                          label={rec.priority.toUpperCase()}
                          size="small"
                          color={getPriorityColor(rec.priority)}
                          sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {renderTextWithDataPoints(rec.title, rec.dataPoints)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                        <strong>Action:</strong> {renderTextWithDataPoints(rec.action, rec.dataPoints)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        <strong>Expected Impact:</strong> {renderTextWithDataPoints(rec.impact, rec.dataPoints)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default AIInsights;
